import { useAtomValue } from "jotai";
import Cookies from "js-cookie";
import type { PageProps } from "next";
import type { PartialPropsOf } from "react";
import { useEffect, useState } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import { parseQueryString } from "shared/lib/utils";

import { PrintCustomContents } from "scmp-app/components/article/article-render/consts";
import { PrintArticleBlockquoteQuote } from "scmp-app/components/article/article-render/print-article/blockquote-quote";
import { PrintArticleUnsupportedElement } from "scmp-app/components/article/article-render/print-article/unsupported-element";
import { ArticleSeo } from "scmp-app/components/article/article-seo";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { Div } from "scmp-app/components/schema-render/common/div";
import { Image } from "scmp-app/components/schema-render/common/image";
import type { RenderFunction } from "scmp-app/components/schema-render/content";
import { ContentSchemaRender } from "scmp-app/components/schema-render/content";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import { rosettaAtom } from "scmp-app/lib/rosetta";
import { useTrackPageView } from "scmp-app/pages/article/hooks";
import type { printArticlePageQuery } from "scmp-app/queries/__generated__/printArticlePageQuery.graphql";

import {
  Body,
  StyledArticleSections,
  StyledArticleSourceHeader,
  StyledContentHeadline,
  StyledContentSponsorTag,
  StyledContentSubHeadline,
  StyledPrintArticleAuthorBlock,
  StyledPrintArticleFooter,
  StyledPrintArticleMainImage,
  StyledPrintArticlePaywall,
} from "./styles";

const SCMPPrintAccessCookiesKey = "ipa"; // From IP Print Access experience run js
const query = graphql`
  query printArticlePageQuery(
    $entityId: String!
    $customContents: [CustomContentInput]
    $bodyType: BodyFormatTypeEnum
  ) {
    article(filter: { entityId: $entityId }) {
      entityId
      body(customContents: $customContents, type: $bodyType) {
        json
      }
      urlAlias
      ...articleSectionsContent
      ...articleSeoArticle @arguments(customContents: $customContents, bodyType: $bodyType)
      ...articleSourceHeaderArticle
      ...contentSchemaRenderContent
      ...contentSubHeadlineContent
      ...contentHeadlineContent
      ...mainImagePrintArticleArticle
      ...authorBlockPrintArticleArticle
      ...contentSponsorTagContent
      ...articlePianoPaywallContent
      ...scmpYoutubeContent
      ...hooksTrackPageViewArticle
    }
  }
`;
const PrintArticlePage: RelayComponent<unknown, printArticlePageQuery> = ({ preloadedQuery }) => {
  const { article } = usePreloadedQuery(query, preloadedQuery);

  const asyncRosettaState = useAtomValue(rosettaAtom);
  const isScmpSubscriber = asyncRosettaState?.result?.user?.isSCMPSubscriber ?? false;

  const [hasPrintAccess, setHasPrintAccess] = useState(false);
  useEffect(() => {
    if (asyncRosettaState?.status === "loading") return;

    const hasPrintIPAccess = Cookies.get(SCMPPrintAccessCookiesKey) === "true" || false;
    setHasPrintAccess(hasPrintIPAccess || isScmpSubscriber);
  }, [asyncRosettaState?.status, isScmpSubscriber]);

  useTrackPageView(article, undefined, `/print${article.urlAlias}`);

  const body = article.body?.json ?? [];
  const previewBody = body.reduce((output, item) => {
    if (output.length === 2) return output;

    if (item.type === "p") {
      output.push(item);
    }
    return output;
  }, [] as Schema);

  const divRenderFunction: RenderFunction<PartialPropsOf<typeof Div>> = (
    props,
    keyIndex,
    index,
    children,
    schemaNode,
    parentSchemaNode,
  ) => {
    if (schemaNode.attribs?.class === "scmp-photo-gallery") {
      return (
        <PrintArticleUnsupportedElement index={index} key={keyIndex} schemaNode={schemaNode} />
      );
    }
    return (
      <Div
        {...props}
        index={index}
        key={keyIndex}
        parentSchemaNode={parentSchemaNode}
        schemaNode={schemaNode}
      >
        {children}
      </Div>
    );
  };

  const imageRenderFunction: RenderFunction<PartialPropsOf<typeof Image>> = (
    props,
    keyIndex,
    index,
    children,
    schemaNode,
    parentSchemaNode,
  ) => {
    const source = props?.src ?? "";
    const gifUrlRegex = /(\S+\.gif)(\?\S*)?$/i;

    if (gifUrlRegex.test(source)) {
      return (
        <PrintArticleUnsupportedElement index={index} key={keyIndex} schemaNode={schemaNode} />
      );
    }
    return (
      <Image
        {...props}
        index={index}
        key={keyIndex}
        parentSchemaNode={parentSchemaNode}
        schemaNode={schemaNode}
      >
        {children}
      </Image>
    );
  };

  return (
    <>
      <ArticleSeo reference={article} />
      <div>
        <StyledArticleSourceHeader reference={article} />
        <StyledArticleSections reference={article} />
        <StyledContentHeadline reference={article} />
        <StyledContentSubHeadline reference={article} />
        <StyledContentSponsorTag reference={article} />
        <StyledPrintArticleAuthorBlock reference={article} />
        <StyledPrintArticleMainImage
          imageProps={{
            $aspectRatio: "326/223",
            lazyload: false,
          }}
          reference={article}
        />
        <Body>
          <ContentSchemaRender
            extraComponentMap={{
              blockquote: PrintArticleUnsupportedElement,
              "blockquote-quote": PrintArticleBlockquoteQuote,
              iframe: PrintArticleUnsupportedElement,
              "inline-widget": PrintArticleUnsupportedElement,
              table: PrintArticleUnsupportedElement,
            }}
            extraRenderFunctionMap={{
              div: divRenderFunction,
              img: imageRenderFunction,
            }}
            reference={article}
            schema={hasPrintAccess ? body : previewBody}
          />
        </Body>
        {!hasPrintAccess && <StyledPrintArticlePaywall />}
        <StyledPrintArticleFooter />
      </div>
    </>
  );
};

PrintArticlePage.displayName = "PrintArticlePage";

const pageProps: PageProps = {
  appInitializerConfiguration: {
    theme: "print-article",
  },
};

export default withRelay(PrintArticlePage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext(context) {
    return {
      customContents: PrintCustomContents,
      entityId: parseQueryString(context.query.entityId) ?? "",
    };
  },
});
